import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Phone, Mail, ExternalLink, Smile, Eye } from 'lucide-react';
import SEO from '../components/SEO';

const Dental = () => {
  return (
    <div className="dental-page">
      <SEO 
        title="Dental & Vision Insurance"
        description="Comprehensive dental and vision insurance plans. Standalone coverage or Medicare supplemental benefits. Affordable plans for individuals, families, and seniors."
        keywords="dental insurance, vision insurance, dental plans, vision plans, eye care insurance, dental coverage Michigan, vision coverage"
        path="/dental"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-teal-50 to-blue-50 py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <img 
                src="/images/dental-1.png" 
                alt="Dental and Vision Coverage" 
                className="rounded-lg shadow-xl w-full object-cover"
              />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">Dental & Vision Coverage</h1>
              <p className="text-2xl text-gray-800 mb-8">
                Affordable dental and vision insurance to keep you healthy and smiling
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why You Need Coverage */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Why Dental and Vision Coverage Matters</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Card className="border-2 border-teal-200">
              <CardContent className="p-8">
                <Smile className="w-12 h-12 mb-4 text-teal-600" />
                <h3 className="text-2xl font-bold text-teal-600 mb-4">Dental Coverage</h3>
                <p className="text-gray-700 mb-4">
                  Regular dental care is essential for overall health. Dental problems can lead to serious health issues if left untreated.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">✓</span>
                    <span>Preventive care (cleanings, exams)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">✓</span>
                    <span>Basic procedures (fillings, extractions)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">✓</span>
                    <span>Major services (crowns, bridges, dentures)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-teal-600 font-bold">✓</span>
                    <span>Orthodontics (some plans)</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200">
              <CardContent className="p-8">
                <Eye className="w-12 h-12 mb-4 text-blue-600" />
                <h3 className="text-2xl font-bold text-blue-600 mb-4">Vision Coverage</h3>
                <p className="text-gray-700 mb-4">
                  Good vision is crucial for quality of life. Regular eye exams can detect serious health conditions early.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">✓</span>
                    <span>Routine eye exams</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">✓</span>
                    <span>Eyeglasses (frames and lenses)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">✓</span>
                    <span>Contact lenses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">✓</span>
                    <span>Discounts on LASIK and other procedures</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Shop Plans Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Shop Dental Plans</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Card className="border-2 border-teal-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-3xl font-bold text-teal-600 mb-3">Ameritas</h3>
                  <p className="text-xl font-semibold text-gray-900 mb-4">Comprehensive Dental & Vision</p>
                  <p className="text-gray-600">Comprehensive dental coverage with nationwide network</p>
                </div>
                <div className="space-y-4">
                  <p className="text-gray-700"><strong>Features:</strong></p>
                  <ul className="space-y-2 text-gray-700 mb-6">
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600">•</span>
                      <span>No waiting periods on preventive care</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600">•</span>
                      <span>Coverage for major procedures</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal-600">•</span>
                      <span>Flexible plan options</span>
                    </li>
                  </ul>
                  <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                    <a href="https://myplan.ameritas.com/id/010Z4038" target="_blank" rel="noopener noreferrer">
                      Shop Ameritas Plans <ExternalLink className="ml-2" size={16} />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-200 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-3xl font-bold text-blue-600 mb-2">Delta Dental</h3>
                  <p className="text-gray-600">Trusted name in dental insurance with extensive provider network</p>
                </div>
                <div className="space-y-4">
                  <p className="text-gray-700"><strong>Features:</strong></p>
                  <ul className="space-y-2 text-gray-700 mb-6">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600">•</span>
                      <span>Largest dentist network</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600">•</span>
                      <span>Preventive care covered at 100%</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600">•</span>
                      <span>Orthodontic coverage available</span>
                    </li>
                  </ul>
                  <Button asChild className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                    <a href="https://www.mysmilecoverage.com/nbp/pabdullah/" target="_blank" rel="noopener noreferrer">
                      Shop Delta Dental Plans <ExternalLink className="ml-2" size={16} />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-gradient-to-r from-teal-50 to-blue-50 border-2 border-teal-200">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Need Help Choosing?</h3>
              <p className="text-lg text-gray-700 mb-6">
                We'll help you compare plans and find the best coverage for your needs and budget.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 text-white">
                  <a href="tel:734.209.6712">
                    <Phone className="mr-2" />
                    Call: 734.209.6712
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-teal-600 text-teal-600 hover:bg-teal-50">
                  <a href="https://calendly.com/llplans/30min" target="_blank" rel="noopener noreferrer">
                    Schedule Consultation <ExternalLink className="ml-2" size={16} />
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">Ready to Protect Your Smile and Vision?</h2>
          <p className="text-xl text-gray-700 mb-8">
            Contact us today for personalized recommendations
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2 text-gray-700 text-lg">
              <Mail size={20} />
              <span>Email 24/7: <a href="mailto:Paulette@llplans.com" className="text-teal-600 hover:text-teal-700 font-semibold">Paulette@llplans.com</a></span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dental;